import React, { useState, useEffect } from 'react';
import { CreditCard, Search, Calendar, Clock, Edit2, Filter, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

export const SubscriptionsList: React.FC = () => {
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [plansConfigs, setPlansConfigs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    // Modais
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<any>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadSubscriptions();
        loadPlansConfig();
    }, []);

    const loadSubscriptions = async () => {
        try {
            setLoading(true);
            const res = await api.get('/banana/subscriptions');
            setSubscriptions(res.data.subscriptions || []);
        } catch (error) {
            console.error('Erro ao carregar assinaturas:', error);
            setSubscriptions([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPlansConfig = async () => {
        try {
            const res = await api.get('/banana/plans-config');
            setPlansConfigs(res.data.plans || []);
        } catch (error) {
            console.error('Erro ao carregar config de planos:', error);
        }
    };

    const handleUpdateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        try {
            setSaving(true);
            await api.patch(`/banana/subscriptions/${editingUser.id}`, {
                plan_type: editingUser.plan_type,
                subscription_status: editingUser.subscription_status,
                subscription_expires_at: editingUser.subscription_expires_at
            });
            setToast({ msg: 'Assinatura atualizada com sucesso!', type: 'success' });
            setIsUserModalOpen(false);
            loadSubscriptions();
        } catch (error) {
            setToast({ msg: 'Erro ao atualizar assinatura.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleUpdatePlan = async (planId: string, data: any) => {
        try {
            setSaving(true);
            await api.patch(`/banana/plans-config/${planId}`, data);
            setToast({ msg: 'Regras do plano atualizadas!', type: 'success' });
            loadPlansConfig();
        } catch (error) {
            setToast({ msg: 'Erro ao salvar configuração.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const getDaysRemaining = (expiryDate: string | null) => {
        if (!expiryDate) return null;
        const now = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getStatusStyles = (status: string, daysRemaining: number | null) => {
        if (status === 'expired' || (daysRemaining !== null && daysRemaining < 0)) {
            return 'bg-red-500/20 text-red-400 border-red-500/20';
        }
        if (daysRemaining !== null && daysRemaining <= 5) {
            return 'bg-orange-500/20 text-orange-400 border-orange-500/20';
        }
        if (status === 'active') {
            return 'bg-green-500/20 text-green-400 border-green-500/20';
        }
        return 'bg-gray-500/20 text-gray-400 border-white/5';
    };

    const filteredSubs = (subscriptions || []).filter(s => {
        const matchesSearch =
            s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.full_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const days = getDaysRemaining(s.subscription_expires_at);
        const isExpired = s.subscription_status === 'expired' || (days !== null && days < 0);

        if (filterStatus === 'all') return matchesSearch;
        if (filterStatus === 'active') return matchesSearch && s.subscription_status === 'active' && !isExpired;
        if (filterStatus === 'expired') return matchesSearch && isExpired;
        return matchesSearch;
    });

    return (
        <div className="p-6">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <CreditCard className="text-orange-500" /> Gestão de Assinaturas
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Monitore e controle os planos ativos do SaaS.</p>
                </div>

                <div className="flex gap-3 w-full md:w-auto">
                    <button
                        onClick={() => setIsConfigModalOpen(true)}
                        className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-2 px-4 text-white text-sm font-medium transition-all flex items-center gap-2"
                    >
                        <Filter size={16} className="text-orange-500" /> Configurar Planos
                    </button>
                    <div className="relative flex-grow md:flex-grow-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar cliente..."
                            className="bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white text-sm w-full md:w-64 focus:border-orange-500/50 outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        className="bg-white/5 border border-white/10 rounded-xl py-2 px-4 text-white text-sm outline-none focus:border-orange-500/50 transition-all cursor-pointer"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="all">Todos Status</option>
                        <option value="active">Ativos</option>
                        <option value="expired">Expirados</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/5 border border-white/10 p-5 rounded-2xl">
                    <span className="text-gray-500 text-xs font-medium uppercase tracking-wider block mb-1">Total Ativos</span>
                    <div className="flex items-end gap-2">
                        <span className="text-3xl font-bold text-white">
                            {(subscriptions || []).filter(s => s.subscription_status === 'active').length}
                        </span>
                        <span className="text-green-500 text-sm font-medium mb-1">Clientes</span>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 p-5 rounded-2xl text-orange-500/80">
                    <span className="text-gray-500 text-xs font-medium uppercase tracking-wider block mb-1">Próximos a Vencer (5d)</span>
                    <div className="flex items-end gap-2 text-orange-500">
                        <span className="text-3xl font-bold italic">
                            {(subscriptions || []).filter(s => {
                                const days = getDaysRemaining(s.subscription_expires_at);
                                return days !== null && days > 0 && days <= 5;
                            }).length}
                        </span>
                        <span className="text-sm font-medium mb-1 opacity-70">Atenção</span>
                    </div>
                </div>
                <div className="bg-white/5 border border-white/10 p-5 rounded-2xl text-red-500/80">
                    <span className="text-gray-500 text-xs font-medium uppercase tracking-wider block mb-1">Assinaturas Expiradas</span>
                    <div className="flex items-end gap-2 text-red-500">
                        <span className="text-3xl font-bold italic">
                            {(subscriptions || []).filter(s => {
                                const days = getDaysRemaining(s.subscription_expires_at);
                                return s.subscription_status === 'expired' || (days !== null && days < 0);
                            }).length}
                        </span>
                        <span className="text-sm font-medium mb-1 opacity-70">bloqueados</span>
                    </div>
                </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
                <table className="w-full text-left border-collapse">
                    <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5 uppercase">
                        <tr>
                            <th className="p-5 font-semibold tracking-wider">Cliente</th>
                            <th className="p-5 font-semibold tracking-wider">Plano Atual</th>
                            <th className="p-5 font-semibold tracking-wider">Vencimento</th>
                            <th className="p-5 font-semibold tracking-wider">Faltam</th>
                            <th className="p-5 font-semibold tracking-wider text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500 animate-pulse">Carregando assinaturas...</td></tr>
                        ) : filteredSubs.length === 0 ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500">Nenhuma assinatura corresponde aos filtros.</td></tr>
                        ) : filteredSubs.map(sub => {
                            const days = getDaysRemaining(sub.subscription_expires_at);
                            return (
                                <tr key={sub.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors group">
                                    <td className="p-5">
                                        <div className="flex flex-col">
                                            <span className="text-white font-semibold flex items-center gap-2">
                                                {sub.full_name || 'Sem Nome'}
                                                {days !== null && days < 0 && <AlertCircle size={14} className="text-red-500" />}
                                            </span>
                                            <small className="text-gray-500 font-mono tracking-tight">{sub.email}</small>
                                        </div>
                                    </td>
                                    <td className="p-5">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest border ${sub.plan_type === 'pro' ? 'border-orange-500/50 text-orange-400 bg-orange-500/10' :
                                                sub.plan_type === 'lifetime' ? 'border-purple-500/50 text-purple-400 bg-purple-500/10' :
                                                    'border-white/10 text-gray-400 bg-white/5'
                                                }`}>
                                                {sub.plan_type.toUpperCase()}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-5">
                                        <div className="flex items-center gap-2 text-gray-400">
                                            <Calendar size={14} className="opacity-50" />
                                            {sub.subscription_expires_at ? new Date(sub.subscription_expires_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : 'N/A'}
                                        </div>
                                    </td>
                                    <td className="p-5">
                                        {days === null ? (
                                            <span className="text-gray-600">--</span>
                                        ) : (
                                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border font-bold text-xs ${getStatusStyles(sub.subscription_status, days)}`}>
                                                <Clock size={12} />
                                                {days < 0 ? 'Expirado' : `${days} dias`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-5 text-right">
                                        <button
                                            onClick={() => {
                                                setEditingUser(sub);
                                                setIsUserModalOpen(true);
                                            }}
                                            className="p-2.5 bg-white/5 hover:bg-orange-500/20 text-gray-400 hover:text-orange-500 rounded-xl transition-all border border-transparent hover:border-orange-500/30"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="mt-6 text-gray-600 text-[10px] font-medium uppercase tracking-[0.2em] text-center">
                Hotlize SaaS Core • Gestão Automatizada • {new Date().getFullYear()}
            </p>

            {/* MODAL: Configurar Planos (Regras Globais) */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#0f0f0f] border border-white/10 w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-white/5">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Filter className="text-orange-500" size={20} /> Regras dos Planos
                            </h2>
                            <button onClick={() => setIsConfigModalOpen(false)} className="text-gray-500 hover:text-white transition-colors">✕</button>
                        </div>
                        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
                            {plansConfigs.map(plan => (
                                <div key={plan.id} className="bg-white/5 border border-white/5 p-4 rounded-2xl flex flex-col md:flex-row gap-4 items-start md:items-center">
                                    <div className="flex-grow">
                                        <span className="text-xs font-black text-orange-500 uppercase tracking-widest">{plan.slug}</span>
                                        <input
                                            type="text"
                                            defaultValue={plan.name}
                                            className="bg-transparent text-white font-bold text-lg outline-none w-full border-b border-transparent focus:border-orange-500/30"
                                            onBlur={(e) => handleUpdatePlan(plan.id, { name: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex gap-4 items-end">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Visível Assinar</label>
                                            <button
                                                type="button"
                                                onClick={() => handleUpdatePlan(plan.id, { is_active: !(plan.is_active !== false) })}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border w-20 outline-none focus:border-orange-500/50 ${plan.is_active !== false ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-white/5 border-white/10 text-gray-500'}`}
                                            >
                                                {plan.is_active !== false ? 'Sim' : 'Não'}
                                            </button>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Máx Bots</label>
                                            <input
                                                type="number"
                                                defaultValue={plan.max_bots}
                                                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm w-20 outline-none focus:border-orange-500/50"
                                                onBlur={(e) => handleUpdatePlan(plan.id, { max_bots: e.target.value })}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <label className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Taxa (%)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                defaultValue={parseFloat(plan.fee_percentage)}
                                                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm w-20 outline-none focus:border-orange-500/50"
                                                onBlur={(e) => handleUpdatePlan(plan.id, { fee_percentage: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="p-6 bg-white/5 border-t border-white/10 text-right">
                            <button
                                onClick={() => setIsConfigModalOpen(false)}
                                className="bg-orange-500 hover:bg-orange-600 text-black font-bold py-2.5 px-8 rounded-xl transition-all shadow-lg shadow-orange-500/20 text-sm"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Editar Usuário (Assinatura Específica) */}
            {isUserModalOpen && editingUser && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <form onSubmit={handleUpdateUser} className="bg-[#0f0f0f] border border-white/10 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-white/10 bg-white/5 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Edit2 className="text-orange-500" size={20} /> Editar Assinatura
                            </h2>
                            <button type="button" onClick={() => setIsUserModalOpen(false)} className="text-gray-500 hover:text-white transition-colors">✕</button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl border border-white/5">
                                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-500 font-bold text-xl">
                                    {editingUser.full_name?.charAt(0) || 'U'}
                                </div>
                                <div>
                                    <h3 className="text-white font-bold">{editingUser.full_name || editingUser.email}</h3>
                                    <p className="text-gray-500 text-xs">{editingUser.email}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Plano</label>
                                    <select
                                        className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                        value={editingUser.plan_type}
                                        onChange={(e) => setEditingUser({ ...editingUser, plan_type: e.target.value })}
                                    >
                                        <option value="free">Free</option>
                                        <option value="teste">Teste</option>
                                        <option value="starter">Starter</option>
                                        <option value="pro">Pro Scale</option>
                                        <option value="lifetime">Lifetime</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Status</label>
                                    <select
                                        className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-orange-500/50"
                                        value={editingUser.subscription_status}
                                        onChange={(e) => setEditingUser({ ...editingUser, subscription_status: e.target.value })}
                                    >
                                        <option value="active">Ativo</option>
                                        <option value="inactive">Inativo</option>
                                        <option value="expired">Expirado</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Data de Expiração</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                                    <input
                                        type="date"
                                        className="bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white text-sm w-full outline-none focus:border-orange-500/50"
                                        value={editingUser.subscription_expires_at ? new Date(editingUser.subscription_expires_at).toISOString().split('T')[0] : ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, subscription_expires_at: e.target.value })}
                                    />
                                </div>
                                <p className="text-[10px] text-gray-600 italic mt-1">* Deixe vazio para planos Lifetime.</p>
                            </div>
                        </div>
                        <div className="p-6 bg-white/5 border-t border-white/10 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setIsUserModalOpen(false)}
                                className="flex-grow py-3 px-4 rounded-xl border border-white/10 text-white font-bold text-sm hover:bg-white/5 transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex-grow py-3 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50"
                            >
                                {saving ? 'Salvando...' : 'Salvar Alterações'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};
